import type { TourStep } from './steps';

/**
 * Pure step-navigation logic for the feature tour, extracted from the provider
 * so skipping/counting rules are unit-testable without a DOM. `isPresent` is
 * injected (the provider checks the real document for a visible anchor).
 */

/**
 * Index of the first step at or beyond `from` (scanning in `dir`) whose anchor
 * is present, or null when none remains in that direction.
 */
export function findPresentStep(
  steps: TourStep[],
  from: number,
  dir: 1 | -1,
  isPresent: (target: string) => boolean,
): number | null {
  for (let i = from; i >= 0 && i < steps.length; i += dir) {
    if (isPresent(steps[i]!.target)) return i;
  }
  return null;
}

/** What the start poll should do on this tick. */
export type StartDecision =
  | { action: 'start'; index: number }
  | { action: 'wait' }
  | { action: 'giveUp' };

/**
 * Whether the tour may open yet, and where.
 *
 * The forward walk only ever moves in `dir`, so a step whose anchor mounts a
 * beat after the tour opens is skipped and never returned to (#510). Rather
 * than teach the engine to backtrack, we refuse to open while a surface says
 * one of the itinerary's anchors is still being decided: `isPending(target)`
 * is true while the data that settles whether that anchor renders at all is
 * still in flight (the Dashboard's getting-started card is the only such
 * anchor today). Waiting also keeps the "N of M" denominator from ticking up
 * mid-tour, which was the visible half of the same bug.
 *
 * `exhausted` is the poll's patience running out: at that point we open with
 * whatever is on screen, since a tour missing a step beats no tour at all.
 */
export function decideStart(
  steps: TourStep[],
  isPresent: (target: string) => boolean,
  isPending: (target: string) => boolean,
  exhausted: boolean,
): StartDecision {
  if (!exhausted && steps.some(s => isPending(s.target))) return { action: 'wait' };
  const first = findPresentStep(steps, 0, 1, isPresent);
  if (first !== null) return { action: 'start', index: first };
  return exhausted ? { action: 'giveUp' } : { action: 'wait' };
}

/**
 * Position for the "N of M" eyebrow, counted over present steps only — so a
 * tour that skips hidden anchors still reads 1, 2, 3 … without gaps. The
 * current step always counts as present (its anchor may have just unmounted;
 * the overlay advances on its own in that case).
 */
export function stepPosition(
  steps: TourStep[],
  index: number,
  isPresent: (target: string) => boolean,
): { n: number; of: number } {
  let n = 0;
  let of = 0;
  steps.forEach((s, i) => {
    if (i === index || isPresent(s.target)) {
      of += 1;
      if (i <= index) n += 1;
    }
  });
  return { n, of };
}
